import React from 'react';
import { injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import leafletCss from 'leaflet/dist/leaflet.css';
import AdminShell from './AdminShell';
import { EmptyState } from './Overlay';
import { socket } from '../App';
import { mapTiles as configuredTiles } from '../../config';

// ponytail: src/config.js is gitignored, so a deployed server can still be on an
// older copy without mapTiles. Fall back instead of crashing the whole screen.
const mapTiles = configuredTiles || {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap contributors'
};

const QUERY = `{
  available: getMapViewData(id: "availablePartners", period: "today", limit: 500) {
    status errorMessage results { id userType isActive activeStatus profile { firstName lastName lat lng } }
  }
  busy: getMapViewData(id: "unAvailablePartners", period: "today", limit: 500, onlyActive: true) {
    status errorMessage results { id userType isActive activeStatus activeBookingId profile { firstName lastName lat lng } }
  }
}`;

const copy = locale => locale === 'ar' ? {
  operations: 'العمليات', title: 'التتبع المباشر',
  lead: 'مواقع مقدمي الخدمة وحالتهم في الوقت الفعلي.', map: 'الخريطة',
  available: 'متاح', busy: 'في مهمة', loadError: 'تعذر تحميل مواقع مقدمي الخدمة.',
  live: 'مباشر', fallback: 'تحديث كل 30 ثانية', tileError: 'تعذر تحميل الخريطة. تحقق من إعداد مزود الخريطة.',
  noProviders: 'لا توجد مواقع لمقدمي الخدمة', noProvidersMsg: 'لا توجد إحداثيات متاحة حالياً.'
} : {
  operations: 'Operations', title: 'Live tracking',
  lead: 'Real-time provider positions and availability.', map: 'Map',
  available: 'Available', busy: 'On a job', loadError: 'Provider locations could not be loaded.',
  live: 'Live', fallback: '30s refresh', tileError: 'Map tiles could not be loaded. Check the map provider configuration.',
  noProviders: 'No provider locations', noProvidersMsg: 'No provider coordinates are available right now.'
};

class ProviderMap extends React.Component {
  componentDidMount() {
    this.L = require('leaflet');
    this.map = this.L.map(this.el).setView([25.2048, 55.2708], 10);
    const tiles = this.L.tileLayer(mapTiles.url, {
      maxZoom: 19,
      attribution: mapTiles.attribution
    });
    tiles.on('tileerror', this.props.onTileError);
    tiles.on('load', this.props.onTileLoad);
    tiles.addTo(this.map);
    this.layer = this.L.layerGroup().addTo(this.map);
    this.draw();
  }

  componentDidUpdate(prev) {
    if (prev.markers !== this.props.markers) this.draw();
  }

  componentWillUnmount() { if (this.map) this.map.remove(); }

  draw = () => {
    const points = [];
    this.layer.clearLayers();
    this.props.markers.forEach(provider => {
      const lat = Number(provider.profile && provider.profile.lat);
      const lng = Number(provider.profile && provider.profile.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const busy = provider.activeStatus === 'active';
      const icon = this.L.divIcon({
        className: `provider-pin ${busy ? 'busy' : 'available'}`,
        html: '<span></span>', iconSize: [24, 24], iconAnchor: [12, 12]
      });
      const name = [provider.profile.firstName, provider.profile.lastName].filter(Boolean).join(' ');
      this.L.marker([lat, lng], { icon, title: name }).addTo(this.layer);
      points.push([lat, lng]);
    });
    if (points.length && !this.hasFitBounds) {
      this.map.fitBounds(points, { padding: [36, 36], maxZoom: 14 });
      this.hasFitBounds = true;
    }
  };

  render() {
    return <div className="provider-map" role="region" aria-label={this.props.label}
      ref={el => { this.el = el; }} />;
  }
}

class LiveTracking extends React.Component {
  state = { available: [], busy: [], loading: true, error: null, tileError: false, socketConnected: false };

  componentDidMount() {
    this.onSocketConnect = () => this.setState({ socketConnected: true });
    this.onSocketDisconnect = () => this.setState({ socketConnected: false });
    socket.on('connect', this.onSocketConnect);
    socket.on('disconnect', this.onSocketDisconnect);
    if (socket.connected) this.onSocketConnect();
    this.load();
    this.timer = setInterval(this.load, 30000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    socket.removeListener('connect', this.onSocketConnect);
    socket.removeListener('disconnect', this.onSocketDisconnect);
    (this.bookingEvents || []).forEach(event => socket.removeListener(event, this.onPartnerLocation));
  }

  syncBookingSockets = busy => {
    (this.bookingEvents || []).forEach(event => socket.removeListener(event, this.onPartnerLocation));
    this.bookingEvents = busy.filter(provider => provider.activeBookingId)
      .map(provider => `partnerLocation-${provider.activeBookingId}`);
    this.bookingEvents.forEach(event => socket.on(event, this.onPartnerLocation));
  };

  onPartnerLocation = update => {
    const lat = Number(update && update.lat);
    const lng = Number(update && update.lng);
    if (!update || !update.partnerId || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    this.setState(state => ({
      busy: state.busy.map(provider => provider.id === update.partnerId
        ? { ...provider, profile: { ...provider.profile, lat, lng } } : provider)
    }));
  };

  load = async () => {
    const json = await fetch('/graphql', {
      method: 'post', credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: QUERY })
    }).then(r => r.json()).catch(() => null);
    const data = json && json.data;
    if (!data || !data.available || !data.busy || data.available.status !== 200 || data.busy.status !== 200) {
      this.setState({ loading: false, error: true });
      return;
    }
    const busy = data.busy.results || [];
    this.setState({
      available: data.available.results || [], busy,
      loading: false, error: null
    }, () => this.syncBookingSockets(busy));
  };

  render() {
    const { available, busy, loading, error, tileError, socketConnected } = this.state;
    const t = copy(this.props.intl.locale);
    const markers = available.concat(busy);

    return (
      <AdminShell current="tracking" breadcrumb={[t.operations, t.title]}
        title={t.title} lead={t.lead}
        actions={[
          <span className={`tag ${socketConnected ? 'ok' : 'warn'}`} key="live"><i />
            {socketConnected ? t.live : t.fallback}</span>,
          <span className="tag amber" key="busy"><i />{busy.length} {t.busy}</span>
        ]}>
        {error && <div className="notice danger"><span className="ni">!</span>
          <span className="nt">{t.loadError}</span></div>}
        {tileError && <div className="notice danger"><span className="ni">!</span>
          <span className="nt">{t.tileError}</span></div>}
        <section className="card">
          <div className="card-h">
            <h2>{t.map}</h2>
            <div className="sp">
              <span className="tag ok"><i />{available.length} {t.available}</span>
              <span className="tag amber"><i />{busy.length} {t.busy}</span>
            </div>
          </div>
          <div className="card-b flush" style={{ minHeight: 560 }}>
            {loading ? <EmptyState icon="route" title="…" />
              : markers.length === 0
                ? <EmptyState icon="route" title={t.noProviders} msg={t.noProvidersMsg} />
                : <ProviderMap markers={markers} label={t.map}
                  onTileError={() => this.setState({ tileError: true })}
                  onTileLoad={() => this.setState({ tileError: false })} />}
          </div>
        </section>
      </AdminShell>
    );
  }
}

export default injectIntl(withStyles(leafletCss)(LiveTracking));
